import { useEffect, useState } from "react";
import {
  Store, User, Info, Save, Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle, UserCog, Tag,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { DEFAULT_LABEL_CONFIG, invalidateLabelConfigCache } from "./printOS";

export default function SettingsView() {
  const [tab, setTab] = useState("store");

  return (
    <div className="p-6 space-y-4" data-testid="settings-view">
      <div>
        <h2 className="font-heading text-2xl font-bold text-slate-900">Configurações</h2>
        <p className="text-sm text-slate-500 mt-0.5">Personalize os dados da loja, a sua conta, usuários e a impressão de etiquetas.</p>
      </div>

      <div className="inline-flex rounded-lg bg-slate-100 p-0.5 flex-wrap">
        {[
          { key: "store", label: "Dados da loja", icon: Store },
          { key: "account", label: "Conta", icon: User },
          { key: "users", label: "Usuários", icon: UserCog },
          { key: "label", label: "Etiqueta", icon: Tag },
          { key: "about", label: "Sobre", icon: Info },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`settings-tab-${key}`}
            className={`px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition-all ${
              tab === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {tab === "store" && <StoreSettings />}
      {tab === "account" && <AccountSettings />}
      {tab === "users" && <UsersSettings />}
      {tab === "label" && <LabelSettings />}
      {tab === "about" && <AboutSettings />}
    </div>
  );
}

// ─── Store ────────────────────────────────────────────────────────────────────
function StoreSettings() {
  const [data, setData] = useState({
    storeName: "",
    cnpj: "",
    address: "",
    phone: "",
    email: "",
    warrantyDays: 90,
    footerNote: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setData((d) => ({ ...d, ...r.data })))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { ...data, warrantyDays: Number(data.warrantyDays) || 0 });
      setToast({ ok: true, msg: "Dados da loja salvos!" });
    } catch {
      setToast({ ok: false, msg: "Erro ao salvar." });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 2500);
    }
  };

  const field = (k, label, hint) => (
    <div>
      <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">{label}</label>
      <input
        value={data[k] ?? ""}
        onChange={(e) => setData({ ...data, [k]: e.target.value })}
        data-testid={`store-${k}`}
        className="mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
      />
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );

  if (loading) return <div className="text-sm text-slate-400 py-8">Carregando...</div>;

  return (
    <div className="max-w-3xl bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-slate-50">
        <h3 className="font-heading font-semibold text-slate-900">Dados da loja</h3>
        <p className="text-xs text-slate-500 mt-0.5">Aparecem no cabeçalho das impressões (OS, etiqueta, recibo)</p>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {field("storeName", "Nome da loja", "Ex.: Phone Lab · Assistência Técnica")}
        {field("cnpj", "CNPJ", "Opcional")}
        <div className="md:col-span-2">{field("address", "Endereço completo")}</div>
        {field("phone", "Telefone / WhatsApp")}
        {field("email", "E-mail")}
        <div>
          <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">
            Garantia padrão (dias)
          </label>
          <input
            type="number"
            min={0}
            value={data.warrantyDays}
            onChange={(e) => setData({ ...data, warrantyDays: e.target.value })}
            data-testid="store-warrantyDays"
            className="mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none tabular-nums"
          />
          <p className="text-[11px] text-slate-400 mt-1">Aparece no rodapé da OS impressa</p>
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">
            Mensagem de rodapé (opcional)
          </label>
          <textarea
            value={data.footerNote ?? ""}
            onChange={(e) => setData({ ...data, footerNote: e.target.value })}
            rows={2}
            data-testid="store-footerNote"
            className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>
      <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
        {toast ? (
          <span
            className={`text-xs font-semibold inline-flex items-center gap-1.5 ${
              toast.ok ? "text-emerald-700" : "text-rose-700"
            }`}
          >
            {toast.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {toast.msg}
          </span>
        ) : <span />}
        <button
          onClick={save}
          disabled={saving}
          data-testid="store-save"
          className="h-10 px-5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar dados
        </button>
      </div>
    </div>
  );
}

// ─── Account ──────────────────────────────────────────────────────────────────
function AccountSettings() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [newer, setNewer] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const submit = async () => {
    setErr("");
    setOk("");
    if (newer.length < 4) return setErr("A nova senha precisa ter ao menos 4 caracteres.");
    if (newer !== confirm) return setErr("A confirmação não bate com a nova senha.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { currentPassword: current, newPassword: newer });
      setOk("Senha alterada com sucesso!");
      setCurrent(""); setNewer(""); setConfirm("");
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao alterar senha.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-slate-50">
        <h3 className="font-heading font-semibold text-slate-900">Conta</h3>
        <p className="text-xs text-slate-500 mt-0.5">Logado como <b className="text-slate-800">{user?.username}</b></p>
      </div>
      <div className="p-6 space-y-4">
        <div>
          <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Senha atual</label>
          <div className="relative mt-1.5">
            <Lock className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
            <input
              type={showPw ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="pw-current"
              className="w-full h-10 rounded-lg border border-slate-300 pl-9 pr-10 text-sm focus:border-blue-500 focus:outline-none"
            />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Nova senha</label>
          <input
            type={showPw ? "text" : "password"}
            value={newer}
            onChange={(e) => setNewer(e.target.value)}
            data-testid="pw-new"
            className="mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Confirmar nova senha</label>
          <input
            type={showPw ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="pw-confirm"
            className="mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
        {err && (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            {err}
          </div>
        )}
        {ok && (
          <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            {ok}
          </div>
        )}
      </div>
      <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex justify-end">
        <button
          onClick={submit}
          disabled={saving || !current || !newer}
          data-testid="pw-save"
          className="h-10 px-5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Alterar senha
        </button>
      </div>
    </div>
  );
}

// ─── Users management (admin only) ────────────────────────────────────────
function UsersSettings() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data);
      if (r.data.length && !selectedId) selectUser(r.data[0]);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao carregar usuários.");
    } finally { setLoading(false); }
  };

  const selectUser = (u) => {
    setSelectedId(u.id);
    setUsername(u.username || "");
    setName(u.name || "");
    setNewPassword(""); setConfirmPassword("");
    setErr(""); setOk("");
  };

  useEffect(() => { load(); }, []);

  const selected = users.find((u) => u.id === selectedId);

  const roleLabel = (r) => r === "manager" ? "Gerente" : r === "employee" ? "Funcionário" : r;

  const submit = async () => {
    setErr(""); setOk("");
    if (!selected) return;
    if (newPassword && newPassword !== confirmPassword) {
      return setErr("A confirmação de senha não bate com a nova senha.");
    }
    if (newPassword && newPassword.length < 4) {
      return setErr("A senha precisa ter ao menos 4 caracteres.");
    }
    const body = {};
    if (username && username !== selected.username) body.username = username.trim().toLowerCase();
    if (name && name !== selected.name) body.name = name.trim();
    if (newPassword) body.newPassword = newPassword;
    if (Object.keys(body).length === 0) {
      return setErr("Nada foi alterado.");
    }
    setSaving(true);
    try {
      const r = await api.patch(`/users/${selected.id}`, body);
      setUsers((prev) => prev.map((u) => (u.id === r.data.id ? r.data : u)));
      setUsername(r.data.username);
      setName(r.data.name);
      setNewPassword(""); setConfirmPassword("");
      setOk(`Dados de ${r.data.name} atualizados com sucesso!`);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erro ao salvar.");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-slate-400 py-8">Carregando...</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4 max-w-4xl">
      {/* Lista de usuários */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-slate-50">
          <h3 className="font-heading font-semibold text-slate-900 text-sm">Contas</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Clique para editar</p>
        </div>
        <ul>
          {users.map((u) => (
            <li key={u.id}>
              <button
                onClick={() => selectUser(u)}
                data-testid={`user-pick-${u.username}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 transition-colors ${
                  selectedId === u.id ? "bg-blue-50" : "hover:bg-slate-50"
                }`}
              >
                <div className="font-semibold text-sm text-slate-800">{u.name || u.username}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                  <span className="font-mono">{u.username}</span>
                  <span>·</span>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-semibold ${
                    u.role === "manager" ? "bg-purple-50 text-purple-700 border border-purple-100" : "bg-slate-100 text-slate-700 border border-slate-200"
                  }`}>{roleLabel(u.role)}</span>
                </div>
              </button>
            </li>
          ))}
          {users.length === 0 && (
            <li className="p-4 text-xs italic text-slate-400 text-center">Nenhum usuário cadastrado.</li>
          )}
        </ul>
      </div>

      {/* Editor */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-slate-50">
          <h3 className="font-heading font-semibold text-slate-900 inline-flex items-center gap-2">
            <UserCog className="w-4 h-4 text-blue-600" />
            {selected ? `Editando: ${selected.name}` : "Selecione uma conta"}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Altere usuário, nome e senha da conta selecionada.</p>
        </div>

        {selected && (
          <>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Usuário (login)</label>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="user-username"
                  className="mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
                />
                <p className="text-[11px] text-slate-400 mt-1">Aparece na tela de login. Sempre em minúsculas.</p>
              </div>

              <div>
                <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Nome de exibição</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="user-name"
                  className="mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div className="border-t border-slate-100 pt-4">
                <div className="text-[11px] uppercase font-bold text-slate-500 tracking-wider mb-1.5">Trocar senha (opcional)</div>
                <p className="text-[11px] text-slate-400 mb-3">Deixe em branco para manter a senha atual.</p>

                <div>
                  <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Nova senha</label>
                  <div className="relative mt-1.5">
                    <Lock className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
                    <input
                      type={showPw ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      data-testid="user-new-pw"
                      autoComplete="new-password"
                      className="w-full h-10 rounded-lg border border-slate-300 pl-9 pr-10 text-sm focus:border-blue-500 focus:outline-none"
                    />
                    <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">Confirmar nova senha</label>
                  <input
                    type={showPw ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    data-testid="user-confirm-pw"
                    autoComplete="new-password"
                    className="mt-1.5 w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              {err && (<div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="user-error">{err}</div>)}
              {ok && (<div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="user-ok">{ok}</div>)}
            </div>

            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex justify-end">
              <button
                onClick={submit}
                disabled={saving}
                data-testid="user-save"
                className="h-10 px-5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Salvar alterações
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ─── Label Settings (impressão de etiquetas 40x40mm) ─────────────────────
function LabelSettings() {
  const [cfg, setCfg] = useState(DEFAULT_LABEL_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const label = r.data?.label || {};
      setCfg({ ...DEFAULT_LABEL_CONFIG, ...label });
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { label: cfg });
      invalidateLabelConfigCache();
      setToast({ ok: true, msg: "Configurações da etiqueta salvas!" });
    } catch (e) {
      setToast({ ok: false, msg: e.response?.data?.detail || "Erro ao salvar." });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 2500);
    }
  };

  const reset = () => setCfg({ ...DEFAULT_LABEL_CONFIG });

  const num = (key, label, hint) => (
    <div>
      <label className="text-[11px] uppercase font-bold text-slate-500 tracking-wider">{label}</label>
      <div className="flex items-center gap-2 mt-1.5">
        <input
          type="range" min="4" max="14" step="0.5"
          value={cfg[key]}
          onChange={(e) => setCfg({ ...cfg, [key]: parseFloat(e.target.value) })}
          className="flex-1"
          data-testid={`label-${key}-range`}
        />
        <input
          type="number" min="4" max="14" step="0.5"
          value={cfg[key]}
          onChange={(e) => setCfg({ ...cfg, [key]: parseFloat(e.target.value) || 0 })}
          data-testid={`label-${key}`}
          className="w-20 h-9 rounded-md border border-slate-300 px-2.5 text-sm text-right focus:border-blue-500 focus:outline-none tabular-nums"
        />
        <span className="text-xs text-slate-500 w-6">pt</span>
      </div>
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );

  const toggle = (key, label, hint) => (
    <label className="flex items-start gap-2.5 cursor-pointer">
      <input
        type="checkbox"
        checked={!!cfg[key]}
        onChange={(e) => setCfg({ ...cfg, [key]: e.target.checked })}
        data-testid={`label-${key}`}
        className="w-4 h-4 mt-0.5 accent-blue-600"
      />
      <div>
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        {hint && <div className="text-[11px] text-slate-500">{hint}</div>}
      </div>
    </label>
  );

  if (loading) return <div className="text-sm text-slate-400 py-8">Carregando...</div>;

  return (
    <div className="max-w-3xl bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-slate-50">
        <h3 className="font-heading font-semibold text-slate-900 inline-flex items-center gap-2">
          <Tag className="w-4 h-4 text-blue-600" /> Etiqueta de impressão (40mm × 40mm)
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Ajuste o tamanho das fontes e o que aparece em cada etiqueta impressa. Se algum texto ficar cortado, diminua o valor.
        </p>
      </div>

      <div className="p-6 space-y-5">
        <div>
          <div className="text-[11px] uppercase font-bold text-slate-500 tracking-wider mb-3">Tamanhos de fonte (pt)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {num("clientSize", "Nome do cliente", "Texto maior — nome que aparece em destaque")}
            {num("deviceSize", "Aparelho (modelo · cor)")}
            {num("servicesSize", "Serviços (lista com marcadores)")}
            {num("headerSize", "Cabeçalho (Nº OS + Data)")}
            {num("sectionLabelSize", "Rótulos de seção (ex.: 'Serviços')")}
            {num("noteSize", "Problema e observações")}
            {num("imeiSize", "IMEI (rodapé)")}
          </div>
        </div>

        <div className="border-t border-slate-100 pt-5">
          <div className="text-[11px] uppercase font-bold text-slate-500 tracking-wider mb-3">O que exibir</div>
          <div className="space-y-3">
            {toggle("showProblem", "Mostrar problema relatado", "Descrição do defeito informado pelo cliente")}
            {toggle("showObservation", "Mostrar observação interna", "Notas internas do técnico (não aparece na via do cliente da OS)")}
            {toggle("showIMEI", "Mostrar últimos 4 dígitos do IMEI", "Ajuda a conferir o aparelho na hora da entrega")}
          </div>
        </div>
      </div>

      <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
        {toast ? (
          <span className={`text-xs font-semibold inline-flex items-center gap-1.5 ${toast.ok ? "text-emerald-700" : "text-rose-700"}`}>
            {toast.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {toast.msg}
          </span>
        ) : <span />}
        <div className="flex items-center gap-2">
          <button
            onClick={reset}
            data-testid="label-reset"
            className="h-10 px-4 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200"
          >
            Restaurar padrão
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="label-save"
            className="h-10 px-5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── About ────────────────────────────────────────────────────────────────────
function AboutSettings() {
  return (
    <div className="max-w-xl bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
      <img src="/logo-sm.png" alt="Phone Lab" className="h-14 w-auto object-contain mb-4" />
      <h3 className="font-heading font-bold text-slate-900 text-lg">Sistema de Gestão · Phone Lab</h3>
      <p className="text-sm text-slate-500 mt-1">
        Software de gestão para assistência técnica de celulares — controle de OS, clientes,
        vendas de peças, fechamento por cliente e financeiro completo com despesas.
      </p>
      <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
        <li>• Ordens de serviço com fluxo Recebido → Pronto → Entregue</li>
        <li>• Impressão de OS e etiqueta para o aparelho</li>
        <li>• WhatsApp integrado para avisar cliente e enviar recibos</li>
        <li>• Fechamento por cliente com CSV/PDF e cobrança automática</li>
        <li>• Financeiro com receita, custos e despesas fixas / funcionários</li>
      </ul>
      <div className="mt-5 pt-4 border-t border-slate-100 text-xs text-slate-400">
        Versão 1.0 · © {new Date().getFullYear()}
      </div>
    </div>
  );
}
